// Administration: who may sign in, their API keys, runs and storage. Computers and KVMs are on the Computers page,
// where an administrator sees and manages everyone's.
import { useEffect, useState } from "react";
import { api, type ApiKey, type Me, type Storage, type UserT } from "./api";
import { Runs } from "./Runs";
const ago = (t: number | null) => {
  if (!t) return "never";
  const s = Date.now() / 1000 - t;
  return s < 120 ? "just now" : s < 7200 ? `${Math.round(s / 60)} min ago` : s < 172800 ? `${Math.round(s / 3600)} h ago` : new Date(t * 1000).toLocaleDateString();
};

type Tab = "setup" | "runs" | "storage";
const TABS: [Tab, string][] = [["setup", "People"], ["runs", "Runs"], ["storage", "Storage"]];

export function Admin({ me }: { me: Me }) {
  const [tab, setTab] = useState<Tab>(() => {
    try { return (localStorage.getItem("vk.admin") as Tab) || "setup"; } catch { return "setup"; }
  });
  useEffect(() => { try { localStorage.setItem("vk.admin", tab); } catch { /* private mode */ } }, [tab]);
  return (
    <div className="page"><div className={`page-inner ${tab === "runs" ? "wide" : ""}`}>
      <div className="page-head">
        <h1>Admin</h1>
        <nav className="tabs">
          {TABS.map(([k, l]) => <button key={k} className={`tab ${tab === k ? "on" : ""}`} onClick={() => setTab(k)}>{l}</button>)}
        </nav>
      </div>
      {tab === "setup" && <>
        <p className="lede">People who may sign in. Each adds and shares their own computers and KVMs (Computers);
          administrators see and manage all of them there.</p>
        <Users me={me} />
        <AdminKeys />
      </>}
      {tab === "runs" && <Runs />}
      {tab === "storage" && <StorageView />}
    </div></div>
  );
}

const size = (b: number) => b < 1024 ** 2 ? `${Math.round(b / 1024)} KB` : b < 1024 ** 3 ? `${(b / 1024 ** 2).toFixed(1)} MB` : `${(b / 1024 ** 3).toFixed(2)} GB`;
const KINDS: [string, string][] = [["runs", "Run records and screens"], ["takeovers", "Take-overs (no run)"],
  ["session_logs", "Session logs"], ["transcripts", "Claude transcripts"], ["datasets", "Training data"],
  ["command_line", "Command-line runs"], ["database", "Database"]];

function StorageView() {
  const [st, setSt] = useState<Storage | null>(null);
  const [preview, setPreview] = useState<{ removed: number; freed: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.storage().then(setSt).catch(() => undefined);
  useEffect(() => { load(); api.clean(true).then(setPreview).catch(() => undefined); }, []);
  const clean = () => {
    setBusy(true);
    api.clean(false).then(() => { load(); setPreview({ removed: 0, freed: 0 }); }).finally(() => setBusy(false));
  };
  if (!st) return <p className="muted">Loading…</p>;
  const total = st.usage.total || 1;
  const p = st.policy;
  return (
    <>
      <p className="lede">Space used on the server, and how long things are kept. Clean-up runs once a day; calibration and
        learned pointers are never removed.</p>
      <table className="grid">
        <thead><tr><th>What</th><th className="num">Size</th><th>Share</th></tr></thead>
        <tbody>
          {KINDS.map(([k, l]) => (
            <tr key={k}><td>{l}</td><td className="num">{size(st.usage[k] ?? 0)}</td>
              <td><span className="meter-cell"><span className="meter-track"><span className="meter-fill"
                style={{ width: `${(100 * (st.usage[k] ?? 0)) / total}%` }} /></span></span></td></tr>
          ))}
          <tr><td><strong>Total</strong></td><td className="num"><strong>{size(st.usage.total ?? 0)}</strong></td><td /></tr>
        </tbody>
      </table>
      <h2>Keeping</h2>
      <div className="kv" style={{ gridTemplateColumns: "150px 1fr" }}>
        <span className="label">Runs</span><span>{p.runs_days} days</span>
        <span className="label">👎 and take-overs</span><span>{p.flagged_days} days</span>
        <span className="label">Training data</span><span>{p.datasets_days_after_export} days after it is sent{st.training ? ` to ${st.training}` : " (no training host set: kept until one is)"}</span>
        <span className="label">Cap</span><span>{p.max_gb ? `${p.max_gb} GB` : "none"}</span>
        <span className="label">Last clean-up</span><span>{st.last_clean ? `${new Date(st.last_clean.t * 1000).toLocaleString()} · ${st.last_clean.removed} removed, ${size(st.last_clean.freed)} freed` : "not yet"}</span>
        <span className="label">Last sent</span><span>{st.last_export ? new Date(st.last_export * 1000).toLocaleString() : "never"}</span>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
        <button className="btn" disabled={busy || !preview?.removed} onClick={clean}>{busy ? "Cleaning…" : "Clean up now"}</button>
        <span className="note">{preview == null ? "" : preview.removed ? `${preview.removed} items past their time, ${size(preview.freed)}` : "Nothing is past its time."}</span>
      </div>
    </>
  );
}

function Users({ me }: { me: Me }) {
  const [users, setUsers] = useState<UserT[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [err, setErr] = useState("");
  const load = () => api.users().then(setUsers).catch((e) => setErr(String(e.message)));
  useEffect(() => { load(); }, []);
  const act = (p: Promise<unknown>) => p.then(load).then(() => setErr("")).catch((e) => setErr(String(e.message)));

  return (
    <>
      <h2>People</h2>
      <table className="grid">
        <thead><tr><th>Email</th><th>Role</th><th>Status</th><th>Last seen</th><th>Added by</th><th className="num">API keys</th><th /></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.email}>
              <td>{u.email}{u.email === me.email && <span className="muted"> (you)</span>}</td>
              <td>
                <select className="field" value={u.role} disabled={u.email === me.email}
                  onChange={(e) => act(api.updateUser(u.email, { role: e.target.value }))}>
                  <option value="user">user</option><option value="admin">admin</option>
                </select>
              </td>
              <td><span className={`dot ${u.disabled ? "err" : "ok"}`} /> {u.disabled ? "disabled" : "active"}</td>
              <td className="muted">{ago(u.last_seen)}</td>
              <td className="muted">{u.added_by ?? ""}</td>
              <td className="num">{u.keys || <span className="muted">0</span>}</td>
              <td style={{ textAlign: "right" }}>
                {u.email !== me.email && (
                  <>
                    <button className="btn quiet" onClick={() => act(api.updateUser(u.email, { disabled: !u.disabled }))}>{u.disabled ? "Enable" : "Disable"}</button>
                    <button className="btn quiet danger" onClick={() => confirm(`Remove ${u.email}?`) && act(api.removeUser(u.email))}>Remove</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="form" style={{ gridTemplateColumns: "1fr 110px auto" }}>
        <input className="field" placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <select className="field" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="user">user</option><option value="admin">admin</option>
        </select>
        <button className="btn primary" disabled={!email.includes("@")}
          onClick={() => act(api.addUser(email.trim(), role).then(() => setEmail("")))}>Invite</button>
      </div>
      {err && <div className="error-line">{err}</div>}
    </>
  );
}

// Every live API key (each person's own Claude Code over MCP): an administrator may revoke any of them.
export function AdminKeys() {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [err, setErr] = useState("");
  const load = () => api.adminKeys().then(setKeys).catch((e) => setErr(String(e.message)));
  useEffect(() => { load(); }, []);
  if (!keys || keys.length === 0) return null;
  const day = (t: number | null) => (t ? new Date(t * 1000).toLocaleDateString() : "never");
  return (
    <>
      <h2>API keys</h2>
      <table className="grid">
        <thead><tr><th>Owner</th><th>Name</th><th>Key</th><th>Made</th><th>Last used</th><th /></tr></thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id}>
              <td>{k.owner}</td><td>{k.name}</td><td className="mono muted">{k.prefix}…</td>
              <td className="muted">{day(k.created_at)}</td><td className="muted">{day(k.last_used)}</td>
              <td style={{ textAlign: "right" }}>
                <button className="btn quiet danger" onClick={() => confirm(`Revoke ${k.owner}'s key "${k.name}"?`)
                  && api.revokeKey(k.id).then(load).catch((e) => setErr(String(e.message)))}>Revoke</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {err && <div className="error-line">{err}</div>}
    </>
  );
}
